import type {
  AlarmsConsolidatedByGroup,
  EquipmentDowntimeGroup,
  EquipmentType,
  PlantDowntimeItem,
  UsinaUnavailabilityData,
} from "../types";
import {
  calculateAverageDowntimeSeconds,
  formatSecondsToDowntime,
  parseDowntimeToSeconds,
} from "./timeFormat";

export type UsinaChartRow = {
  usinaId: number;
  usinaName: string;
};

const EQUIPMENT_CONFIG: {
  equipmentType: EquipmentType;
  label: string;
  color: string;
  field: keyof Pick<
    UsinaUnavailabilityData,
    "inversorsUnavailability" | "trackersUnavailability" | "stringsUnavailability"
  >;
}[] = [
  {
    equipmentType: "inverters",
    label: "Inversores",
    color: "#EF4444",
    field: "inversorsUnavailability",
  },
  {
    equipmentType: "trackers",
    label: "Trackers",
    color: "#10B981",
    field: "trackersUnavailability",
  },
  {
    equipmentType: "strings",
    label: "Strings",
    color: "#F97316",
    field: "stringsUnavailability",
  },
];

function getDurationForEquipment(
  usina: UsinaUnavailabilityData | undefined,
  field: (typeof EQUIPMENT_CONFIG)[number]["field"],
): AlarmsConsolidatedByGroup | undefined {
  return usina?.[field];
}

function usinaRowHasTrackers(
  usinaId: number,
  usinaHasTrackersByRopInputId?: Map<number, boolean>,
): boolean {
  if (!usinaHasTrackersByRopInputId) return true;
  return usinaHasTrackersByRopInputId.get(usinaId) ?? false;
}

export function mapClusterUsinasToEquipmentDowntime(
  usinaRows: UsinaChartRow[],
  usinasUnavailability: UsinaUnavailabilityData[] | undefined,
  usinaHasTrackersByRopInputId?: Map<number, boolean>,
): EquipmentDowntimeGroup[] {
  const unavByUsinaId = new Map<number, UsinaUnavailabilityData>();
  for (const u of usinasUnavailability ?? []) {
    unavByUsinaId.set(u.usinaId, u);
  }

  return EQUIPMENT_CONFIG.flatMap((config) => {
    const eligibleRows =
      config.equipmentType === "trackers"
        ? usinaRows.filter((row) =>
            usinaRowHasTrackers(row.usinaId, usinaHasTrackersByRopInputId),
          )
        : usinaRows;

    if (config.equipmentType === "trackers" && eligibleRows.length === 0) {
      return [];
    }

    const data: PlantDowntimeItem[] = eligibleRows.map((row) => {
      const usina = unavByUsinaId.get(row.usinaId);
      const durationStr =
        getDurationForEquipment(usina, config.field)?.totalDuration ?? "";
      const downtimeSeconds = parseDowntimeToSeconds(durationStr);

      return {
        plantId: String(row.usinaId),
        plantName: row.usinaName,
        downtimeSeconds,
        formattedDowntime: formatSecondsToDowntime(downtimeSeconds),
      };
    });

    const averageDowntimeSeconds = calculateAverageDowntimeSeconds(data);

    return [
      {
        equipmentType: config.equipmentType,
        label: config.label,
        color: config.color,
        averageDowntimeSeconds,
        averageDowntimeFormatted: formatSecondsToDowntime(averageDowntimeSeconds),
        data,
      },
    ];
  });
}
